/**
 * Timeline Editor Component
 * Görsel nota timeline editörü - boşluk ekleme/çıkarma
 */

import React, { useMemo, useState, useRef, useEffect } from "react";

interface TimelineEvent {
  ms: number;
  m: number;
  index: number;
}

interface Gap {
  startMs: number;
  endMs: number;
  durationMs: number;
  startIndex: number;
  endIndex: number;
}

interface TimelineEditorProps {
  events: Array<{ ms: number; m: number }>;
  syncMs: number;
  onEventsChange: (newEvents: Array<{ ms: number; m: number }>) => void;
  onSyncMsChange: (newSyncMs: number) => void;
}

// Lane colors
const LANE_COLORS = [
  "#ef4444", // 0 - Kick - red
  "#f97316", // 1 - Snare - orange
  "#eab308", // 2 - Hi-Hat - yellow
  "#22c55e", // 3 - Tom High - green
  "#14b8a6", // 4 - Tom Mid - teal
  "#3b82f6", // 5 - Tom Low - blue
  "#8b5cf6", // 6 - Crash - purple
  "#ec4899", // 7 - Ride - pink
];

const LANE_NAMES = ["Kick", "Snare", "HH", "Tom1", "Tom2", "Tom3", "Crash", "Ride"];

export default function TimelineEditor({
  events,
  syncMs,
  onEventsChange,
  onSyncMsChange,
}: TimelineEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(100); // ms per pixel
  const [scrollX, setScrollX] = useState(0);
  const [selectedGap, setSelectedGap] = useState<Gap | null>(null);
  const [selectedNoteIndex, setSelectedNoteIndex] = useState<number | null>(null);
  const [insertMs, setInsertMs] = useState(0);
  const [insertAmount, setInsertAmount] = useState(1000);
  const [viewWidth, setViewWidth] = useState(800);

  // Nota ekleme state'leri
  const [addNotePosition, setAddNotePosition] = useState("0:00.0");
  const [addNoteEndPosition, setAddNoteEndPosition] = useState("0:00.0");
  const [addNoteCount, setAddNoteCount] = useState(1);
  const [addNoteLanes, setAddNoteLanes] = useState<number[]>([0]); // Default: Kick
  const [showAddNotePanel, setShowAddNotePanel] = useState(false);

  // Container width'i dinle
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setViewWidth(containerRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Convert events to timeline format with absolute ms
  const timelineEvents: TimelineEvent[] = useMemo(() => {
    let absMs = 0;
    return events.map((e, i) => {
      if (i === 0) {
        absMs = e.ms; // First event: dt[0] is absolute
      } else {
        absMs += e.ms; // Subsequent: dt[i] is delta
      }
      return { ms: absMs, m: e.m, index: i };
    });
  }, [events]);

  // Find gaps (>500ms between notes)
  const gaps: Gap[] = useMemo(() => {
    const result: Gap[] = [];
    for (let i = 1; i < timelineEvents.length; i++) {
      const prev = timelineEvents[i - 1];
      const curr = timelineEvents[i];
      const gapMs = curr.ms - prev.ms;
      if (gapMs > 500) {
        result.push({
          startMs: prev.ms,
          endMs: curr.ms,
          durationMs: gapMs,
          startIndex: i - 1,
          endIndex: i,
        });
      }
    }
    return result;
  }, [timelineEvents]);

  // Total duration
  const totalMs = timelineEvents.length > 0 
    ? timelineEvents[timelineEvents.length - 1].ms 
    : 0;

  // Add time at position
  const addTimeAt = (atMs: number, amountMs: number) => {
    const newEvents = events.map((e, i) => {
      const absMs = timelineEvents[i].ms;
      if (i === 0) {
        // First event: increase if adding before it
        if (atMs <= absMs) {
          return { ...e, ms: e.ms + amountMs };
        }
        return e;
      } else {
        // Find the event right after atMs
        const prevAbsMs = timelineEvents[i - 1].ms;
        if (prevAbsMs < atMs && absMs >= atMs) {
          // This is the event right after insertion point
          return { ...e, ms: e.ms + amountMs };
        }
        return e;
      }
    });
    onEventsChange(newEvents);
  };

  // Remove time from gap (shorten gap duration)
  const removeTimeFromGap = (gap: Gap, newDurationMs: number) => {
    const diff = gap.durationMs - newDurationMs;
    if (diff <= 0) return;

    // gap.endIndex'teki event'in delta'sını azalt
    // events[endIndex].ms = delta time (önceki event'e göre fark)
    // Bu delta'yı azaltarak gap'i kısaltıyoruz
    const newEvents = events.map((e, i) => {
      if (i === gap.endIndex) {
        // Delta time = gap duration, bunu newDurationMs'e düşür
        return { ...e, ms: Math.max(10, newDurationMs) };
      }
      return e;
    });
    onEventsChange(newEvents);
  };

  // Add time to gap (extend gap duration)
  const addTimeToGap = (gap: Gap, additionalMs: number) => {
    if (additionalMs <= 0) return;

    const newEvents = events.map((e, i) => {
      if (i === gap.endIndex) {
        // Delta time'ı artır
        return { ...e, ms: e.ms + additionalMs };
      }
      return e;
    });
    onEventsChange(newEvents);
  };

  // Add intro time (before first note)
  const addIntroTime = (amountMs: number) => {
    if (events.length === 0) return;
    const newEvents = [...events];
    newEvents[0] = { ...newEvents[0], ms: newEvents[0].ms + amountMs };
    onEventsChange(newEvents);
  };

  // Delete note at index
  const deleteNote = (index: number) => {
    if (events.length <= 1) return; // En az 1 nota kalmalı
    
    const newEvents = [...events];
    
    // Eğer silinen nota ilk nota değilse, sonraki notanın delta'sını güncelle
    if (index < newEvents.length - 1 && index > 0) {
      // Silinen notanın delta'sını sonraki notaya ekle
      newEvents[index + 1] = {
        ...newEvents[index + 1],
        ms: newEvents[index + 1].ms + newEvents[index].ms,
      };
    }
    
    // Notayı sil
    newEvents.splice(index, 1);
    
    setSelectedNoteIndex(null);
    onEventsChange(newEvents);
  };

  // Lanes'den mask oluştur
  const lanesToMask = (lanes: number[]): number => {
    let mask = 0;
    for (const lane of lanes) {
      mask |= (1 << lane);
    }
    return mask;
  };

  // Belirli ms'ye nota ekle
  const addNoteAtMs = (atMs: number, mask: number) => {
    if (mask === 0) return;
    
    // timelineEvents zaten absolute ms olarak sıralı
    // Yeni notayı doğru pozisyona ekle
    
    // Boş liste kontrolü
    if (events.length === 0) {
      onEventsChange([{ ms: atMs, m: mask }]);
      return;
    }

    // İlk notadan önce mi?
    const firstAbsMs = events[0].ms;
    if (atMs <= firstAbsMs) {
      // İlk nota olarak ekle
      const delta = firstAbsMs - atMs;
      const newEvents = [
        { ms: atMs, m: mask },
        { ms: delta, m: events[0].m },
        ...events.slice(1),
      ];
      onEventsChange(newEvents);
      return;
    }

    // Doğru pozisyonu bul
    let insertIndex = 0;
    let prevAbsMs = events[0].ms;
    
    for (let i = 1; i < events.length; i++) {
      const currAbsMs = prevAbsMs + events[i].ms;
      if (atMs <= currAbsMs) {
        insertIndex = i;
        break;
      }
      prevAbsMs = currAbsMs;
      insertIndex = i + 1;
    }

    // Aynı ms'de nota var mı kontrol et - varsa mask'i birleştir
    if (insertIndex < events.length) {
      const targetAbsMs = insertIndex === 0 ? events[0].ms :
        events.slice(0, insertIndex + 1).reduce((acc, e, i) => i === 0 ? e.ms : acc + e.ms, 0);
      
      if (Math.abs(targetAbsMs - atMs) < 5) {
        // Aynı pozisyonda - mask'i birleştir
        const newEvents = [...events];
        newEvents[insertIndex] = {
          ...newEvents[insertIndex],
          m: newEvents[insertIndex].m | mask,
        };
        onEventsChange(newEvents);
        return;
      }
    }

    // Yeni nota ekle
    const newEvents = [...events];
    
    if (insertIndex === events.length) {
      // Sona ekle
      const lastAbsMs = events.slice(0, insertIndex).reduce((acc, e, i) => i === 0 ? e.ms : acc + e.ms, 0);
      newEvents.push({ ms: atMs - lastAbsMs, m: mask });
    } else {
      // Ortaya ekle
      const prevAbsMsCalc = insertIndex === 0 ? 0 :
        events.slice(0, insertIndex).reduce((acc, e, i) => i === 0 ? e.ms : acc + e.ms, 0);
      
      const newDelta = atMs - prevAbsMsCalc;
      const nextOldDelta = events[insertIndex].ms;
      const nextNewDelta = (insertIndex === 0 ? events[0].ms : prevAbsMsCalc + nextOldDelta) - atMs;
      
      newEvents.splice(insertIndex, 1,
        { ms: insertIndex === 0 ? atMs : newDelta, m: mask },
        { ms: nextNewDelta, m: events[insertIndex].m }
      );
    }
    
    onEventsChange(newEvents);
  };

  // Çoklu nota ekle (belirli aralıkta eşit dağılımlı)
  const addMultipleNotes = (startMs: number, endMs: number, count: number, mask: number) => {
    if (mask === 0 || count <= 0) return;
    
    if (count === 1 || startMs >= endMs) {
      addNoteAtMs(startMs, mask);
      return;
    }

    // Notaların ms değerlerini hesapla
    const interval = (endMs - startMs) / (count - 1);
    const newNoteMsValues: number[] = [];
    for (let i = 0; i < count; i++) {
      newNoteMsValues.push(Math.round(startMs + (interval * i)));
    }

    // Mevcut events'i absolute ms'ye çevir
    const absEvents: Array<{ absMs: number; m: number }> = [];
    let absMs = 0;
    for (let i = 0; i < events.length; i++) {
      if (i === 0) {
        absMs = events[0].ms;
      } else {
        absMs += events[i].ms;
      }
      absEvents.push({ absMs, m: events[i].m });
    }

    // Yeni notaları absolute events'e ekle
    for (const noteMs of newNoteMsValues) {
      // Aynı ms'de nota var mı kontrol et
      const existing = absEvents.find(e => Math.abs(e.absMs - noteMs) < 5);
      if (existing) {
        // Mask'i birleştir
        existing.m |= mask;
      } else {
        // Yeni nota ekle
        absEvents.push({ absMs: noteMs, m: mask });
      }
    }

    // Absolute ms'ye göre sırala
    absEvents.sort((a, b) => a.absMs - b.absMs);

    // Delta formatına geri çevir
    const newEvents: Array<{ ms: number; m: number }> = [];
    for (let i = 0; i < absEvents.length; i++) {
      if (i === 0) {
        newEvents.push({ ms: absEvents[0].absMs, m: absEvents[0].m });
      } else {
        const delta = absEvents[i].absMs - absEvents[i - 1].absMs;
        newEvents.push({ ms: delta, m: absEvents[i].m });
      }
    }

    onEventsChange(newEvents);
  };

  // Format time
  const formatTime = (ms: number) => {
    const sec = Math.floor(ms / 1000);
    const min = Math.floor(sec / 60);
    const s = sec % 60;
    const m = ms % 1000;
    return `${min}:${s.toString().padStart(2, "0")}.${Math.floor(m / 100)}`;
  };

  // Parse time string to ms (format: "0:15.4" = 0 min, 15 sec, 400ms)
  const parseTime = (timeStr: string): number => {
    try {
      const parts = timeStr.split(":");
      let min = 0;
      let secPart = timeStr;
      
      if (parts.length === 2) {
        min = parseInt(parts[0]) || 0;
        secPart = parts[1];
      }
      
      const secParts = secPart.split(".");
      const sec = parseInt(secParts[0]) || 0;
      const decimal = secParts[1] ? parseInt(secParts[1].padEnd(1, "0").slice(0, 1)) : 0;
      
      return (min * 60 * 1000) + (sec * 1000) + (decimal * 100);
    } catch {
      return 0;
    }
  };

  // Render lane dots for a mask
  const renderLaneDots = (mask: number, size: number = 8) => {
    const dots = [];
    for (let lane = 0; lane < 8; lane++) {
      if (mask & (1 << lane)) {
        dots.push(
          <div
            key={lane}
            style={{
              width: size,
              height: size,
              borderRadius: "50%",
              background: LANE_COLORS[lane],
              display: "inline-block",
              marginRight: 2,
            }}
            title={LANE_NAMES[lane]}
          />
        );
      }
    }
    return dots;
  };

  return (
    <div
      style={{
        background: "#1e293b",
        border: "1px solid #334155",
        borderRadius: 16,
        padding: "1rem",
        marginTop: "1rem",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ color: "#94a3b8", fontWeight: 800 }}>
          <span role="img" aria-label="chart">📊</span> Timeline Editor
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ color: "#64748b", fontSize: 12 }}>Zoom:</span>
          <input
            type="range"
            min={10}
            max={500}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: 100 }}
          />
          <span style={{ color: "#94a3b8", fontSize: 12, fontFamily: "monospace" }}>
            {zoom}ms/px
          </span>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 16, marginBottom: 12, fontSize: 12 }}>
        <span style={{ color: "#64748b" }}>
          Events: <span style={{ color: "#f1f5f9" }}>{events.length}</span>
        </span>
        <span style={{ color: "#64748b" }}>
          Duration: <span style={{ color: "#f1f5f9" }}>{formatTime(totalMs)}</span>
        </span>
        <span style={{ color: "#64748b" }}>
          Gaps (&gt;500ms): <span style={{ color: "#f1f5f9" }}>{gaps.length}</span>
        </span>
        <span style={{ color: "#64748b" }}>
          First note at: <span style={{ color: "#10b981" }}>{formatTime(timelineEvents[0]?.ms || 0)}</span>
        </span>
      </div>

      {/* Quick Actions */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <span style={{ color: "#94a3b8", fontSize: 12 }}>Intro'ya ekle:</span>
          <button
            onClick={() => addIntroTime(1000)}
            style={{
              borderRadius: 6,
              background: "#22c55e",
              border: "none",
              color: "white",
              padding: "4px 8px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            +1s
          </button>
          <button
            onClick={() => addIntroTime(5000)}
            style={{
              borderRadius: 6,
              background: "#22c55e",
              border: "none",
              color: "white",
              padding: "4px 8px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            +5s
          </button>
          <button
            onClick={() => addIntroTime(-1000)}
            disabled={timelineEvents[0]?.ms < 1000}
            style={{
              borderRadius: 6,
              background: "#ef4444",
              border: "none",
              color: "white",
              padding: "4px 8px",
              fontSize: 11,
              cursor: timelineEvents[0]?.ms >= 1000 ? "pointer" : "not-allowed",
              opacity: timelineEvents[0]?.ms >= 1000 ? 1 : 0.5,
            }}
          >
            -1s
          </button>
        </div>

        <div style={{ marginLeft: "auto", display: "flex", gap: 4, alignItems: "center" }}>
          <span style={{ color: "#94a3b8", fontSize: 12 }}>Pozisyona ekle (ms):</span>
          <input
            type="number"
            value={insertMs}
            onChange={(e) => setInsertMs(Number(e.target.value))}
            style={{
              width: 80,
              borderRadius: 6,
              background: "#0f172a",
              border: "1px solid #334155",
              padding: "4px 8px",
              color: "#f1f5f9",
              fontSize: 11,
            }}
          />
          <input
            type="number"
            value={insertAmount}
            onChange={(e) => setInsertAmount(Number(e.target.value))}
            placeholder="Miktar"
            style={{
              width: 70,
              borderRadius: 6,
              background: "#0f172a",
              border: "1px solid #334155",
              padding: "4px 8px",
              color: "#f1f5f9",
              fontSize: 11,
            }}
          />
          <button
            onClick={() => addTimeAt(insertMs, insertAmount)}
            style={{
              borderRadius: 6,
              background: "#3b82f6",
              border: "none",
              color: "white",
              padding: "4px 8px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Ekle
          </button>
        </div>

        {/* Nota Ekle Butonu */}
        <button
          onClick={() => setShowAddNotePanel(!showAddNotePanel)}
          style={{
            borderRadius: 6,
            background: showAddNotePanel 
              ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" 
              : "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
            border: "none",
            color: "white",
            padding: "4px 12px",
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <span role="img" aria-label="add">➕</span> Nota Ekle
        </button>
      </div>

      {/* Nota Ekleme Paneli */}
      {showAddNotePanel && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            background: "#0f172a",
            borderRadius: 12,
            border: "1px solid #6366f1",
          }}
        >
          <div style={{ color: "#a5b4fc", fontWeight: 800, marginBottom: 12 }}>
            <span role="img" aria-label="music">🎵</span> Nota Ekle
          </div>

          {/* Lane Seçimi */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6 }}>
              Enstrüman Seç (çoklu seçim için birden fazla tıkla):
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {LANE_NAMES.map((name, lane) => {
                const isSelected = addNoteLanes.includes(lane);
                return (
                  <button
                    key={lane}
                    onClick={() => {
                      if (isSelected) {
                        // Seçimden çıkar (en az 1 kalmalı)
                        if (addNoteLanes.length > 1) {
                          setAddNoteLanes(addNoteLanes.filter(l => l !== lane));
                        }
                      } else {
                        // Seçime ekle
                        setAddNoteLanes([...addNoteLanes, lane]);
                      }
                    }}
                    style={{
                      borderRadius: 6,
                      background: isSelected ? LANE_COLORS[lane] : "#1e293b",
                      border: `2px solid ${LANE_COLORS[lane]}`,
                      color: isSelected ? "#fff" : LANE_COLORS[lane],
                      padding: "4px 10px",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tek Nota / Çoklu Nota */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {/* Tek Nota Ekle */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6 }}>
                Tek Nota Ekle:
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ color: "#64748b", fontSize: 11 }}>Pozisyon:</span>
                <input
                  type="text"
                  value={addNotePosition}
                  onChange={(e) => setAddNotePosition(e.target.value)}
                  placeholder="0:15.4"
                  style={{
                    width: 80,
                    borderRadius: 6,
                    background: "#1e293b",
                    border: "1px solid #334155",
                    padding: "6px 10px",
                    color: "#f1f5f9",
                    fontSize: 12,
                    fontFamily: "monospace",
                  }}
                />
                <button
                  onClick={() => {
                    const mask = lanesToMask(addNoteLanes);
                    const ms = parseTime(addNotePosition);
                    addNoteAtMs(ms, mask);
                  }}
                  style={{
                    borderRadius: 6,
                    background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                    border: "none",
                    color: "white",
                    padding: "6px 14px",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Ekle
                </button>
              </div>
              <div style={{ color: "#64748b", fontSize: 10, marginTop: 4 }}>
                Format: dakika:saniye.desimal (örn: 0:15.4 = 15.4 saniye)
              </div>
            </div>

            {/* Çoklu Nota Ekle */}
            <div style={{ flex: 2, minWidth: 300 }}>
              <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6 }}>
                Çoklu Nota Ekle (Aralıkta Eşit Dağılım):
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ color: "#64748b", fontSize: 11 }}>Başlangıç:</span>
                <input
                  type="text"
                  value={addNotePosition}
                  onChange={(e) => setAddNotePosition(e.target.value)}
                  placeholder="0:00.0"
                  style={{
                    width: 70,
                    borderRadius: 6,
                    background: "#1e293b",
                    border: "1px solid #334155",
                    padding: "6px 10px",
                    color: "#f1f5f9",
                    fontSize: 12,
                    fontFamily: "monospace",
                  }}
                />
                <span style={{ color: "#64748b", fontSize: 11 }}>→ Bitiş:</span>
                <input
                  type="text"
                  value={addNoteEndPosition}
                  onChange={(e) => setAddNoteEndPosition(e.target.value)}
                  placeholder="0:10.0"
                  style={{
                    width: 70,
                    borderRadius: 6,
                    background: "#1e293b",
                    border: "1px solid #334155",
                    padding: "6px 10px",
                    color: "#f1f5f9",
                    fontSize: 12,
                    fontFamily: "monospace",
                  }}
                />
                <input
                  type="number"
                  value={addNoteCount}
                  onChange={(e) => setAddNoteCount(Math.max(1, Number(e.target.value)))}
                  min={1}
                  placeholder="Adet"
                  style={{
                    width: 50,
                    borderRadius: 6,
                    background: "#1e293b",
                    border: "1px solid #334155",
                    padding: "6px 10px",
                    color: "#f1f5f9",
                    fontSize: 12,
                  }}
                />
                <span style={{ color: "#64748b", fontSize: 11 }}>adet</span>
                <button
                  onClick={() => {
                    const mask = lanesToMask(addNoteLanes);
                    const startMs = parseTime(addNotePosition);
                    const endMs = parseTime(addNoteEndPosition);
                    addMultipleNotes(startMs, endMs, addNoteCount, mask);
                  }}
                  style={{
                    borderRadius: 6,
                    background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                    border: "none",
                    color: "white",
                    padding: "6px 14px",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Çoklu Ekle
                </button>
              </div>
              {addNoteCount > 1 && parseTime(addNoteEndPosition) > parseTime(addNotePosition) && (
                <div style={{ color: "#64748b", fontSize: 10, marginTop: 4 }}>
                  Aralık: {Math.round((parseTime(addNoteEndPosition) - parseTime(addNotePosition)) / (addNoteCount - 1))}ms
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => setShowAddNotePanel(false)}
            style={{
              marginTop: 12,
              borderRadius: 6,
              background: "#374151",
              border: "none",
              color: "#f1f5f9",
              padding: "6px 14px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Kapat
          </button>
        </div>
      )}

      {/* Timeline Visual */}
      <div
        ref={containerRef}
        style={{
          background: "#0f172a",
          borderRadius: 12,
          border: "1px solid #334155",
          height: 120,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Time ruler */}
        <div
          style={{
            height: 24,
            borderBottom: "1px solid #334155",
            display: "flex",
            alignItems: "center",
            paddingLeft: 4,
            fontSize: 10,
            color: "#64748b",
            fontFamily: "monospace",
          }}
        >
          {Array.from({ length: Math.ceil(totalMs / 10000) + 1 }).map((_, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                left: (i * 10000) / zoom - scrollX,
                borderLeft: "1px solid #334155",
                paddingLeft: 4,
                height: 24,
                display: "flex",
                alignItems: "center",
              }}
            >
              {formatTime(i * 10000)}
            </div>
          ))}
        </div>

        {/* Events */}
        <div style={{ height: 60, position: "relative" }}>
          {timelineEvents.map((e, i) => {
            const x = e.ms / zoom - scrollX;
            if (x < -20 || x > viewWidth + 20) return null;
            const isSelected = selectedNoteIndex === i;
            return (
              <div
                key={i}
                onClick={() => {
                  setSelectedNoteIndex(isSelected ? null : i);
                  setSelectedGap(null); // Gap seçimini kaldır
                }}
                style={{
                  position: "absolute",
                  left: x,
                  top: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                  padding: "2px 4px",
                  borderRadius: 4,
                  background: isSelected ? "rgba(239, 68, 68, 0.3)" : "transparent",
                  border: isSelected ? "1px solid #ef4444" : "1px solid transparent",
                }}
                title={`${formatTime(e.ms)}\ndt: ${events[i]?.ms}ms\nTıkla: seç / sil`}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {renderLaneDots(e.m, 6)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Gaps */}
        <div style={{ height: 30, position: "relative" }}>
          {gaps.map((gap, i) => {
            const x = gap.startMs / zoom - scrollX;
            const width = gap.durationMs / zoom;
            if (x + width < 0 || x > viewWidth) return null;
            return (
              <div
                key={i}
                onClick={() => setSelectedGap(selectedGap === gap ? null : gap)}
                style={{
                  position: "absolute",
                  left: x,
                  width: Math.max(width, 2),
                  height: 20,
                  background: selectedGap === gap ? "rgba(251, 191, 36, 0.3)" : "rgba(100, 116, 139, 0.2)",
                  borderRadius: 4,
                  border: selectedGap === gap ? "1px solid #fbbf24" : "1px solid #475569",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 9,
                  color: "#94a3b8",
                  overflow: "hidden",
                }}
                title={`Gap: ${(gap.durationMs / 1000).toFixed(1)}s`}
              >
                {width > 30 && `${(gap.durationMs / 1000).toFixed(1)}s`}
              </div>
            );
          })}
        </div>

        {/* Scroll indicator */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 6,
            background: "#1e293b",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: `${(scrollX / ((totalMs / zoom) - viewWidth + 100)) * 100}%`,
              width: `${(viewWidth / (totalMs / zoom)) * 100}%`,
              height: "100%",
              background: "#3b82f6",
              borderRadius: 3,
              minWidth: 20,
            }}
          />
        </div>
      </div>

      {/* Scroll control */}
      <input
        type="range"
        min={0}
        max={Math.max(0, (totalMs / zoom) - viewWidth + 100)}
        value={scrollX}
        onChange={(e) => setScrollX(Number(e.target.value))}
        style={{ width: "100%", marginTop: 8 }}
      />

      {/* Selected gap editor */}
      {selectedGap && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            background: "#0f172a",
            borderRadius: 12,
            border: "1px solid #fbbf24",
          }}
        >
          <div style={{ color: "#fbbf24", fontWeight: 800, marginBottom: 12 }}>
            <span role="img" aria-label="edit">✏️</span> Gap Düzenle
          </div>
          
          {/* Gap info */}
          <div style={{ display: "flex", gap: 16, marginBottom: 12, fontSize: 12 }}>
            <span style={{ color: "#94a3b8" }}>
              Pozisyon: <span style={{ color: "#f1f5f9" }}>{formatTime(selectedGap.startMs)}</span> → <span style={{ color: "#f1f5f9" }}>{formatTime(selectedGap.endMs)}</span>
            </span>
            <span style={{ color: "#94a3b8" }}>
              Mevcut Süre: <span style={{ color: "#10b981", fontWeight: 700 }}>{(selectedGap.durationMs / 1000).toFixed(2)}s</span>
            </span>
          </div>

          {/* Quick adjust buttons */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            <span style={{ color: "#94a3b8", fontSize: 12, alignSelf: "center" }}>Hızlı Ayar:</span>
            
            {/* Decrease buttons */}
            <button
              onClick={() => {
                removeTimeFromGap(selectedGap, Math.max(100, selectedGap.durationMs - 2000));
              }}
              style={{
                borderRadius: 6,
                background: "#dc2626",
                border: "none",
                color: "white",
                padding: "6px 10px",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              -2s
            </button>
            <button
              onClick={() => {
                removeTimeFromGap(selectedGap, Math.max(100, selectedGap.durationMs - 1000));
              }}
              style={{
                borderRadius: 6,
                background: "#ef4444",
                border: "none",
                color: "white",
                padding: "6px 10px",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              -1s
            </button>
            <button
              onClick={() => {
                removeTimeFromGap(selectedGap, Math.max(100, selectedGap.durationMs - 500));
              }}
              style={{
                borderRadius: 6,
                background: "#f87171",
                border: "none",
                color: "white",
                padding: "6px 10px",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              -0.5s
            </button>

            {/* Increase buttons */}
            <button
              onClick={() => {
                addTimeToGap(selectedGap, 500);
              }}
              style={{
                borderRadius: 6,
                background: "#4ade80",
                border: "none",
                color: "#0f172a",
                padding: "6px 10px",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              +0.5s
            </button>
            <button
              onClick={() => {
                addTimeToGap(selectedGap, 1000);
              }}
              style={{
                borderRadius: 6,
                background: "#22c55e",
                border: "none",
                color: "white",
                padding: "6px 10px",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              +1s
            </button>
            <button
              onClick={() => {
                addTimeToGap(selectedGap, 2000);
              }}
              style={{
                borderRadius: 6,
                background: "#16a34a",
                border: "none",
                color: "white",
                padding: "6px 10px",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              +2s
            </button>
          </div>

          {/* Manual input */}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ color: "#94a3b8", fontSize: 12 }}>Elle ayarla (s):</span>
            <input
              type="number"
              step={0.1}
              min={0.1}
              defaultValue={(selectedGap.durationMs / 1000).toFixed(1)}
              id="gap-duration-input"
              style={{
                width: 80,
                borderRadius: 6,
                background: "#1e293b",
                border: "1px solid #334155",
                padding: "6px 10px",
                color: "#f1f5f9",
                fontSize: 12,
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const newSec = Number((e.target as HTMLInputElement).value);
                  if (newSec > 0) {
                    removeTimeFromGap(selectedGap, newSec * 1000);
                  }
                }
              }}
            />
            <button
              onClick={() => {
                const input = document.getElementById('gap-duration-input') as HTMLInputElement;
                const newSec = Number(input?.value || 0);
                if (newSec > 0) {
                  removeTimeFromGap(selectedGap, newSec * 1000);
                }
              }}
              style={{
                borderRadius: 6,
                background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                border: "none",
                color: "white",
                padding: "6px 14px",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Uygula
            </button>
            <button
              onClick={() => setSelectedGap(null)}
              style={{
                borderRadius: 6,
                background: "#374151",
                border: "none",
                color: "#f1f5f9",
                padding: "6px 14px",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Kapat
            </button>
          </div>
        </div>
      )}

      {/* Selected note editor */}
      {selectedNoteIndex !== null && timelineEvents[selectedNoteIndex] && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            background: "#0f172a",
            borderRadius: 12,
            border: "1px solid #ef4444",
          }}
        >
          <div style={{ color: "#ef4444", fontWeight: 800, marginBottom: 12 }}>
            <span role="img" aria-label="note">🎵</span> Nota #{selectedNoteIndex + 1}
          </div>
          
          {/* Note info */}
          <div style={{ display: "flex", gap: 16, marginBottom: 12, fontSize: 12, flexWrap: "wrap" }}>
            <span style={{ color: "#94a3b8" }}>
              Pozisyon: <span style={{ color: "#f1f5f9" }}>{formatTime(timelineEvents[selectedNoteIndex].ms)}</span>
            </span>
            <span style={{ color: "#94a3b8" }}>
              Delta: <span style={{ color: "#10b981", fontWeight: 700 }}>{events[selectedNoteIndex]?.ms}ms</span>
            </span>
            <span style={{ color: "#94a3b8" }}>
              Enstrümanlar: {renderLaneDots(timelineEvents[selectedNoteIndex].m, 8)}
            </span>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={() => deleteNote(selectedNoteIndex)}
              disabled={events.length <= 1}
              style={{
                borderRadius: 6,
                background: events.length > 1 ? "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)" : "#374151",
                border: "none",
                color: "white",
                padding: "8px 16px",
                fontSize: 12,
                fontWeight: 600,
                cursor: events.length > 1 ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span role="img" aria-label="delete">🗑️</span> Notayı Sil
            </button>
            <button
              onClick={() => setSelectedNoteIndex(null)}
              style={{
                borderRadius: 6,
                background: "#374151",
                border: "none",
                color: "#f1f5f9",
                padding: "8px 16px",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Kapat
            </button>
            {events.length <= 1 && (
              <span style={{ color: "#f87171", fontSize: 11 }}>
                (Son nota silinemez)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Event list (compact) */}
      <div style={{ marginTop: 12 }}>
        <div style={{ color: "#64748b", fontSize: 11, marginBottom: 4 }}>
          İlk 20 nota:
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxHeight: 100, overflow: "auto" }}>
          {timelineEvents.slice(0, 20).map((e, i) => (
            <div
              key={i}
              style={{
                background: "#0f172a",
                border: "1px solid #334155",
                borderRadius: 6,
                padding: "4px 8px",
                fontSize: 10,
                color: "#94a3b8",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span style={{ color: "#64748b" }}>#{i + 1}</span>
              <span style={{ color: "#f1f5f9" }}>{formatTime(e.ms)}</span>
              {renderLaneDots(e.m, 5)}
            </div>
          ))}
          {timelineEvents.length > 20 && (
            <div style={{ color: "#64748b", fontSize: 10, padding: "4px 8px" }}>
              +{timelineEvents.length - 20} daha...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
